import { useState, useRef, useEffect, useCallback } from 'react';
import { X, Send, Loader2, Sparkles, Target, BookOpen, FileText, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function renderMarkdown(text) {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/^## (.+)$/gm, '<h3 class="ai-chat-heading">$1</h3>')
    .replace(/^### (.+)$/gm, '<h4 class="ai-chat-subheading">$1</h4>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>\n?)+/g, (match) => `<ul class="ai-chat-list">${match}</ul>`)
    .replace(/\n{2,}/g, '<br/><br/>')
    .replace(/\n/g, '<br/>');
}

const MODE_CONFIG = {
  general: { title: 'Análisis General', icon: Sparkles },
  objetivos: { title: 'Análisis de Objetivos', icon: Target },
  bitacora: { title: 'Análisis de Bitácora', icon: BookOpen },
};

export function AIChatModal({ open, onClose, mode, startSession, sendMessage, session }) {
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [exporting, setExporting] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const handleExportPdf = useCallback(async () => {
    if (!session?.sessionId || exporting) return;
    try {
      setExporting(true);
      const res = await fetch(`${BACKEND_URL}/api/summary/export/${session.sessionId}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Error al exportar PDF');
      const contentDisposition = res.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch ? filenameMatch[1] : `bitacaria_analisis_${mode}.pdf`;
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Export PDF error:', err);
    } finally {
      setExporting(false);
    }
  }, [session?.sessionId, exporting, mode]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [session?.messages, scrollToBottom]);

  useEffect(() => {
    if (open && !session?.loading && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 300);
    }
  }, [open, session?.loading]);

  // Start session when modal opens with a new mode
  useEffect(() => {
    if (open && mode && !session?.sessionId && !session?.loading) {
      startSession(mode);
    }
  }, [open, mode, session?.sessionId, session?.loading, startSession]);

  const handleSend = async () => {
    const msg = input.trim();
    if (!msg || sending || !session?.sessionId) return;
    setInput('');
    setSending(true);
    try {
      await sendMessage(session.sessionId, msg);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!open) return null;

  const config = MODE_CONFIG[mode] || MODE_CONFIG.general;
  const Icon = config.icon;
  const messages = session?.messages || [];
  const isLoading = session?.loading;

  return (
    <div className="ai-chat-overlay" data-testid="ai-chat-overlay">
      <div className="ai-chat-modal" data-testid="ai-chat-modal">
        {/* Header */}
        <div className="ai-chat-header">
          <div className="ai-chat-header-left">
            <Icon className="h-4 w-4" />
            <span className="ai-chat-title">{config.title}</span>
            {session?.sessionId && (
              <span className="ai-chat-session-badge">
                <FileText className="h-3 w-3" /> Documento activo
              </span>
            )}
          </div>
          <div className="ai-chat-header-actions">
            {session?.sessionId && messages.length > 0 && (
              <button
                className="ai-chat-close"
                onClick={handleExportPdf}
                disabled={exporting}
                title="Descargar conversación en PDF"
                data-testid="ai-chat-export-pdf"
              >
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              </button>
            )}
            <button className="ai-chat-close" onClick={onClose} data-testid="ai-chat-close">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="ai-chat-messages" data-testid="ai-chat-messages">
          {isLoading && messages.length === 0 && (
            <div className="ai-chat-phase">
              <Loader2 className="h-5 w-5 animate-spin" />
              <div className="ai-chat-phase-text">
                <span className="ai-chat-phase-title">Generando respaldo y analizando...</span>
                <span className="ai-chat-phase-sub">Creando documento de referencia con toda tu información</span>
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`ai-chat-msg ai-chat-msg-${msg.role}`}>
              {msg.role === 'assistant' ? (
                <div className="ai-chat-msg-content" dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }} />
              ) : (
                <div className="ai-chat-msg-content ai-chat-user-text">{msg.content}</div>
              )}
            </div>
          ))}

          {(isLoading && messages.length > 0) && (
            <div className="ai-chat-msg ai-chat-msg-assistant">
              <div className="ai-chat-msg-content ai-chat-typing">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Analizando...</span>
              </div>
            </div>
          )}

          {sending && (
            <div className="ai-chat-msg ai-chat-msg-assistant">
              <div className="ai-chat-msg-content ai-chat-typing">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Pensando...</span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="ai-chat-input-area">
          <textarea
            ref={textareaRef}
            className="ai-chat-input"
            placeholder="Pregunta algo sobre el análisis..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={2}
            disabled={!session?.sessionId || isLoading}
            data-testid="ai-chat-input"
          />
          <button
            className="ai-chat-send"
            onClick={handleSend}
            disabled={!input.trim() || sending || !session?.sessionId || isLoading}
            data-testid="ai-chat-send"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
